import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, of, timer } from 'rxjs';
import { ForceOrderingAvailableService } from '@core/services/marketplace-ordering/force-ordering-available.service';
import {
  catchError,
  distinctUntilChanged,
  map,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs/operators';

import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { MarketplaceOrderingApiService } from '@core/services/api/marketplaces/marketplace-ordering-api.service';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';

export interface MarketplaceContext {
  ordering_available: boolean;
}

const POLL_INTERVAL_MS = 60_000;
const PREVIEW_CONTEXT: MarketplaceContext = { ordering_available: true };

@Injectable({ providedIn: 'root' })
export class MarketplaceOrderingAvailableService {
  private readonly marketplaceSelector = inject(MarketplaceStoreSelector);
  private readonly apiService = inject(MarketplaceOrderingApiService);
  private readonly cb2PreviewState = inject(Cb2PreviewStateService);
  private readonly forceOrderingAvailable = inject(ForceOrderingAvailableService);

  private readonly consumerCount$ = new BehaviorSubject(0);
  private readonly isPreview$ = toObservable(this.cb2PreviewState.isActive);
  private readonly isForceOrdering$ = this.forceOrderingAvailable.isForceOrderingActive$;

  /**
   * Registers a consumer that needs marketplace ordering availability (shared 60s poll).
   * Used by content-builder visibility and kiosk marketplace ordering-unavailable modal.
   */
  register(): void {
    this.consumerCount$.next(this.consumerCount$.value + 1);
  }

  /** Stops polling when the last consumer unregisters. */
  unregister(): void {
    this.consumerCount$.next(Math.max(0, this.consumerCount$.value - 1));
  }

  /**
   * Single shared poll: GET /marketplaces/{id}/ordering-available every 60s
   * while at least one consumer is registered and marketplaceId is set.
   * CB2 preview skips the API — virtual `preview-journey-*` ids are not real marketplaces.
   */
  readonly marketplaceContext$ = combineLatest({
    hasConsumers: this.consumerCount$.pipe(
      map(count => count > 0),
      distinctUntilChanged(),
    ),
    marketplaceId: this.marketplaceSelector.selectMarketplaceId$,
    isPreview: this.isPreview$,
    isForceOrdering: this.isForceOrdering$,
  }).pipe(
    switchMap(({ hasConsumers, marketplaceId, isPreview, isForceOrdering }) => {
      if (isPreview || isForceOrdering) {
        return of(PREVIEW_CONTEXT);
      }

      if (!hasConsumers || !marketplaceId) {
        return of<MarketplaceContext>({ ordering_available: true });
      }

      return timer(0, POLL_INTERVAL_MS).pipe(
        switchMap(() =>
          this.apiService.getOrderingAvailable$(marketplaceId).pipe(
            map(({ orderable }) => ({ ordering_available: orderable })),
            catchError(() => of<MarketplaceContext>({ ordering_available: true })),
          ),
        ),
        startWith<MarketplaceContext>({ ordering_available: true }),
      );
    }),
    shareReplay({ bufferSize: 1, refCount: false }),
  );
}
